import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { withTranslation, WithTranslation } from 'react-i18next';
import { Add } from '@material-ui/icons';
import { Link as RouterLink, RouteComponentProps } from 'react-router-dom';
import Icon from '@material-ui/core/Icon';
import Button from '@material-ui/core/Button';
import AddIcon from '@material-ui/icons/Add';
// Components
import App from '../../../components/app';
import EnhancedTable from '../../../components/table';
import { HeadCell } from '../../../local-types';
import ButtonTo from '../../../components/buttonTo';
import { TooltipButton } from '../../../components/tooltip-button';
import Listing from '../../../components/listing';
// Modules
import { tableRequestPoints } from '../../../redux/reducers/constants';
import { PrebookGroup, PrebookPlanning } from '../../../redux/reducers/api-types';
import { makeMemoizedGetDatas } from '../../../redux/reducers/table';
// Ressources
import withGlobalStyles from '../../../utils/global-styles';
import StringUtils from '../../../utils/stringUtils';
import { paths, createPath } from '../../paths';
import { clsx } from '../../../utils/htmlUtils';
import UserUtils, { AccountRight, RightRank } from '../../../utils/userUtils';
import { Cell, createCell } from '../../../components/table/table-cell-creator';

const queryString = require('query-string');

interface PrebooksListPageProps extends WithTranslation, RouteComponentProps {
    globalClasses: any;
    prebooks: PrebookGroup[];
}

const PrebooksListPage = ({ t, globalClasses, location, prebooks }: PrebooksListPageProps): JSX.Element => {
    const params = queryString.parse(location.search);

    const isDriverPrebookWrite: boolean = UserUtils.checkRights({
        name: AccountRight.DRIVER_PREBOOK,
        mode: RightRank.W,
    });

    const [currentPrebook, setCurrentPrebook] = React.useState<PrebookGroup>({} as PrebookGroup);
    const [currentPlannings, setCurrentPlannings] = React.useState<PrebookPlanning[]>([]);
    const [currentPlanning, setCurrentPlanning] = React.useState<PrebookPlanning>({} as PrebookPlanning);

    useEffect(() => {
        let plannings = currentPlannings;
        const prebook_id = params.prebook_id;
        const planning_id = params.planning_id;
        if (prebook_id && prebooks && prebooks.find((p) => p.id === prebook_id)) {
            const prebook = prebooks.find((p) => p.id === prebook_id);
            setCurrentPrebook(prebook);
            setCurrentPlannings(prebook.plannings);
            plannings = prebook.plannings;
        } else {
            setCurrentPrebook({} as PrebookGroup);
            setCurrentPlannings([]);
            setCurrentPlanning({} as PrebookPlanning);
        }
        if (planning_id && plannings && plannings.find((p) => p.id === planning_id)) {
            const planning = plannings.find((p) => p.id === planning_id);
            setCurrentPlanning(planning);
        } else {
            setCurrentPlanning({} as PrebookPlanning);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params.prebook_id, params.planning_id, prebooks]);

    //========================================//
    //=============== Handlers ===============//
    //========================================//

    const headerBuilder = (): HeadCell[] => {
        const headers: HeadCell[] = [
            createCell(t, Cell.NAME),
            { id: 'min_prebook_interval', label: t('form.min_prebook_interval'), filterable: false },
            {
                id: 'action',
                label: t('common:action'),
                filterable: false,
                formatCell: (row) => {
                    return (
                        <div>
                            {isDriverPrebookWrite && (
                                <TooltipButton title={t('common:edit')}>
                                    <Button
                                        size="small"
                                        variant="contained"
                                        color="secondary"
                                        style={{ marginRight: 8 }}
                                        component={RouterLink}
                                        to={`${paths.prebooksAdd}?id=${row.id}`}
                                        className={globalClasses.btnAutoWidth}
                                    >
                                        <Icon fontSize="small">edit</Icon>
                                    </Button>
                                </TooltipButton>
                            )}
                            <TooltipButton title={t('common:select')}>
                                <Button
                                    size="small"
                                    variant="contained"
                                    color="secondary"
                                    style={{ marginRight: 8 }}
                                    component={RouterLink}
                                    to={`${paths.prebooks}?prebook_id=${row.id}`}
                                    className={globalClasses.btnAutoWidth}
                                >
                                    <Icon fontSize="small">arrow_forward</Icon>
                                </Button>
                            </TooltipButton>
                        </div>
                    );
                },
            },
        ];
        return headers;
    };

    //=======================================//
    //=============== Renders ===============//
    //=======================================//

    const renderPlanningDays = () => {
        return currentPlannings?.map((planning, index) => {
            return (
                planning && (
                    <div key={planning.id} style={{ display: 'inherit', flex: 1, alignItems: 'center' }}>
                        <div>{`${planning.time_slots?.weekDays.map((day) => ` ${t(`common:days.${day}`)}`)}`}</div>
                        <div style={{ flex: 1 }} />
                        {isDriverPrebookWrite && (
                            <TooltipButton title={t('common:edit')}>
                                <Button
                                    size="small"
                                    variant="contained"
                                    color="secondary"
                                    style={{ marginRight: 8 }}
                                    component={RouterLink}
                                    to={`${createPath(paths.prebooksPlannings, {
                                        id: params.prebook_id,
                                    })}?planning_id=${planning.id}`}
                                    className={globalClasses.btnAutoWidth}
                                >
                                    <Icon fontSize="small">edit</Icon>
                                </Button>
                            </TooltipButton>
                        )}
                        <TooltipButton title={t('common:select')}>
                            <Button
                                size="small"
                                variant="contained"
                                color="secondary"
                                style={{ marginRight: 8 }}
                                component={RouterLink}
                                to={`${paths.prebooks}?prebook_id=${params.prebook_id}&planning_id=${planning.id}`}
                                className={globalClasses.btnAutoWidth}
                            >
                                <Icon fontSize="small">arrow_forward</Icon>
                            </Button>
                        </TooltipButton>
                    </div>
                )
            );
        });
    };

    const renderPlanningDetailsHeader = () => {
        return (
            <div style={{ display: 'flex', marginRight: 20, marginLeft: 20 }}>
                <b style={{ flex: 1 }}>{StringUtils.upperFirst(t('common:hour_from'))}</b>
                <b style={{ flex: 1 }}>{StringUtils.upperFirst(t('common:hour_to'))}</b>
                <b style={{ flex: 1 }}>{t('hours.delay')}</b>
                <b style={{ flex: 1 }}>{t('hours.duration')}</b>
                <div style={{ flex: 1 }} />
                <div style={{ width: 46 }} />
            </div>
        );
    };

    const renderPlanningDetails = () => {
        return currentPlanning?.time_slots?.hours?.map((hour, index) => {
            return (
                <div key={index} style={{ display: 'inherit', flex: 1, alignItems: 'center' }}>
                    <div style={{ flex: 1 }}>{hour.rideDate.start}</div>
                    <div style={{ flex: 1 }}>{hour.rideDate.end}</div>
                    <div style={{ flex: 1 }}>{StringUtils.formatTimeNumber(hour.visibility.delay / 60)}</div>
                    <div style={{ flex: 1 }}>{StringUtils.formatTimeNumber(hour.visibility.duration / 60)}</div>
                    <div style={{ flex: 1 }} />
                    {isDriverPrebookWrite && (
                        <TooltipButton title={t('common:edit')}>
                            <Button
                                size="small"
                                variant="contained"
                                color="secondary"
                                style={{ marginRight: 8 }}
                                component={RouterLink}
                                to={`${createPath(paths.prebooksPlannings, {
                                    id: params.prebook_id,
                                })}?planning_id=${currentPlanning.id}`}
                                className={globalClasses.btnAutoWidth}
                            >
                                <Icon fontSize="small">edit</Icon>
                            </Button>
                        </TooltipButton>
                    )}
                </div>
            );
        });
    };

    return (
        <App
            disableBreadcrumb
            needRights={AccountRight.DRIVER_PREBOOK}
            header={{
                title: t('title'),
                subtitle: t('description'),
                button: (
                    <ButtonTo
                        rights={{ name: AccountRight.DRIVER_PREBOOK, mode: RightRank.W }}
                        startIcon={<Add />}
                        to={`prebooks/add`}
                        text={t('common:add')}
                    />
                ),
            }}
        >
            <div style={{ display: 'flex' }}>
                <EnhancedTable
                    headers={headerBuilder()}
                    type={tableRequestPoints.PREBOOKS}
                    contentProps={{ style: { flex: 1 } }}
                />
                <div style={{ flex: 1 }}>
                    <div className={globalClasses.block} style={{ flex: 1, marginLeft: 40 }}>
                        <div style={{ marginBottom: 30 }}>{`${t('days.title')} ${
                            currentPrebook.name ? ' - ' + currentPrebook.name : ''
                        }`}</div>
                        {isDriverPrebookWrite && (
                            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 30 }}>
                                <TooltipButton title={t('common:add')}>
                                    <Button
                                        size="small"
                                        style={{ marginLeft: 20, marginRight: 14 }}
                                        variant="contained"
                                        color="primary"
                                        component={RouterLink}
                                        disabled={!currentPrebook?.name}
                                        to={createPath(paths.prebooksPlannings, {
                                            id: params.prebook_id,
                                        })}
                                        className={clsx(globalClasses.btnAutoWidth, globalClasses.btnSuccess)}
                                        aria-label="action"
                                    >
                                        <AddIcon />
                                    </Button>
                                </TooltipButton>
                                <b>{t('days.add')}</b>
                            </div>
                        )}
                        <Listing datas={renderPlanningDays()} />
                    </div>
                    <div className={globalClasses.block} style={{ flex: 1, marginLeft: 40 }}>
                        <div style={{ marginBottom: 30 }}>{`${t('hours.title')} ${
                            currentPlanning.time_slots?.weekDays
                                ? ' - ' +
                                  currentPlanning.time_slots?.weekDays.map((day) => ` ${t(`common:days.${day}`)}`)
                                : ''
                        }`}</div>
                        {isDriverPrebookWrite && (
                            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 30 }}>
                                <TooltipButton title={t('common:add')}>
                                    <Button
                                        size="small"
                                        style={{ marginLeft: 20, marginRight: 14 }}
                                        variant="contained"
                                        color="primary"
                                        component={RouterLink}
                                        disabled={!currentPlanning?.time_slots?.weekDays}
                                        to={`${createPath(paths.prebooksPlannings, {
                                            id: params.prebook_id,
                                        })}?planning_id=${currentPlanning.id}`}
                                        className={clsx(globalClasses.btnAutoWidth, globalClasses.btnSuccess)}
                                        aria-label="action"
                                    >
                                        <AddIcon />
                                    </Button>
                                </TooltipButton>
                                <b>{t('hours.add')}</b>
                            </div>
                        )}
                        <Listing header={renderPlanningDetailsHeader()} datas={renderPlanningDetails()} />
                    </div>
                </div>
            </div>
        </App>
    );
};

const PrebooksListPageWithStyleAndTranslation = withGlobalStyles(
    withTranslation(['prebook', 'common'])(PrebooksListPage),
);

const makemapStateToProps = () => {
    const getDatas = makeMemoizedGetDatas();

    const mapStateToProps = (state) => {
        return {
            prebooks: getDatas(state, { type: { reducer: 'prebooks_list' } }),
        };
    };
    return mapStateToProps;
};

const mapDispatchToProps = {};

export default connect(makemapStateToProps, mapDispatchToProps)(PrebooksListPageWithStyleAndTranslation);
